import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { mockPurchaseOrderService } from '@/services/mock/MockPurchaseOrderService';
import { PurchaseOrderRecord } from '@/types/domain/po-management';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ArrowLeft, Save, Truck, CreditCard, Building2, Calendar } from 'lucide-react';
import { ROUTES } from '@/constants/routes';
import { formatInr } from '@/lib/utils';

export const PurchaseOrderEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [po, setPo] = useState<PurchaseOrderRecord | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Form fields
  const [paymentTerms, setPaymentTerms] = useState('');
  const [incoterms, setIncoterms] = useState('');
  const [promisedDeliveryDate, setPromisedDeliveryDate] = useState('');
  const [plant, setPlant] = useState('');
  const [shippingAddress, setShippingAddress] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    const loadPo = async () => {
      if (!id) return;
      try {
        setIsLoading(true);
        const data = await mockPurchaseOrderService.getPoById(id);
        if (!data) {
          navigate(ROUTES.PURCHASE_ORDERS.LIST);
          return;
        }
        setPo(data);
        setPaymentTerms(data.paymentTerms || 'Net 30');
        setIncoterms(data.incoterms || 'DDP');
        setPromisedDeliveryDate(data.promisedDeliveryDate ? data.promisedDeliveryDate.slice(0, 10) : '');
        setPlant(data.plant || 'PLANT-1000');
        setShippingAddress(data.shippingAddress || '100 Enterprise Way, Plant 1000');
        setNotes(data.notes || '');
      } finally {
        setIsLoading(false);
      }
    };

    loadPo();
  }, [id, navigate]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !po) return;

    try {
      setIsSaving(true);
      await mockPurchaseOrderService.updatePurchaseOrder(id, {
        plant,
        promisedDeliveryDate: promisedDeliveryDate ? new Date(promisedDeliveryDate).toISOString() : po.promisedDeliveryDate,
        shippingAddress,
        paymentTerms,
        incoterms,
        notes,
      });
      navigate(ROUTES.PURCHASE_ORDERS.DETAILS(id));
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to update purchase order.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-16 text-center text-xs text-foreground-secondary font-medium font-poppins">
        Loading Purchase Order Editor...
      </div>
    );
  }

  if (!po) {
    return (
      <div className="p-16 text-center space-y-4 font-poppins">
        <h2 className="text-lg font-bold text-foreground">Purchase Order Not Found</h2>
        <Button size="sm" onClick={() => navigate(ROUTES.PURCHASE_ORDERS.LIST)} className="font-poppins cursor-pointer">
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Purchase Orders
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-24 font-poppins max-w-5xl mx-auto">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border/60 pb-4">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(ROUTES.PURCHASE_ORDERS.DETAILS(id!))}
            className="rounded-xl border-border hover:bg-muted font-poppins cursor-pointer"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Cancel
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs font-bold text-primary px-2.5 py-0.5 bg-primary/10 rounded-lg border border-primary/20">
                {po.poNumber}
              </span>
              <h1 className="text-xl font-extrabold text-foreground tracking-tight font-poppins">
                Edit Purchase Order
              </h1>
            </div>
            <p className="text-xs text-foreground-secondary font-medium mt-0.5 font-poppins">
              Supplier: <strong className="text-foreground">{po.vendorName}</strong> • Total Value: <strong className="text-primary font-mono">{formatInr(po.totalValue)}</strong>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => navigate(ROUTES.PURCHASE_ORDERS.DETAILS(id!))}
            className="font-poppins cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-po-form"
            size="sm"
            isLoading={isSaving}
            className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold font-poppins cursor-pointer shadow-xs"
          >
            <Save className="h-4 w-4 mr-1.5" /> Save Changes
          </Button>
        </div>
      </div>

      <form id="edit-po-form" onSubmit={handleSave} className="space-y-6">
        {/* Section 1: Fulfillment & Logistics */}
        <Card className="rounded-2xl border border-border bg-card shadow-xs font-poppins">
          <CardHeader className="p-4 border-b border-border/60">
            <CardTitle className="text-sm font-bold text-foreground flex items-center gap-2 font-poppins">
              <Truck className="h-4 w-4 text-primary" />
              Logistics, Plant & Delivery Schedule
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 space-y-4 text-xs font-poppins">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block font-bold text-foreground mb-1.5 flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5 text-primary" /> Promised Delivery Date *
                </label>
                <input
                  type="date"
                  value={promisedDeliveryDate}
                  onChange={(e) => setPromisedDeliveryDate(e.target.value)}
                  required
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-semibold text-foreground focus:ring-2 focus:ring-primary font-poppins"
                />
              </div>

              <div>
                <label className="block font-bold text-foreground mb-1.5 flex items-center gap-1">
                  <Building2 className="h-3.5 w-3.5 text-primary" /> Receiving Plant Location *
                </label>
                <select
                  value={plant}
                  onChange={(e) => setPlant(e.target.value)}
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-semibold text-foreground focus:ring-2 focus:ring-primary font-poppins"
                >
                  <option value="PLANT-1000">PLANT-1000 — Mumbai Central Logistics Center</option>
                  <option value="PLANT-2000">PLANT-2000 — Bangalore Tech Campus</option>
                  <option value="PLANT-3000">PLANT-3000 — Delhi NCR Hub</option>
                  <option value="PLANT-4000">PLANT-4000 — Chennai Industrial Facility</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block font-bold text-foreground mb-1.5">
                Delivery / Gate Receiving Address
              </label>
              <input
                type="text"
                value={shippingAddress}
                onChange={(e) => setShippingAddress(e.target.value)}
                className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-medium text-foreground focus:ring-2 focus:ring-primary font-poppins"
                placeholder="Gate #3, Receiving Bay, Industrial Area"
              />
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Commercial Terms */}
        <Card className="rounded-2xl border border-border bg-card shadow-xs font-poppins">
          <CardHeader className="p-4 border-b border-border/60">
            <CardTitle className="text-sm font-bold text-foreground flex items-center gap-2 font-poppins">
              <CreditCard className="h-4 w-4 text-primary" />
              Commercial Settlement & Incoterms
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 space-y-4 text-xs font-poppins">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Payment Terms *
                </label>
                <select
                  value={paymentTerms}
                  onChange={(e) => setPaymentTerms(e.target.value)}
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-semibold text-foreground focus:ring-2 focus:ring-primary font-poppins"
                >
                  <option value="Net 30">Net 30 Days from Delivery</option>
                  <option value="Net 45">Net 45 Days</option>
                  <option value="Net 60">Net 60 Days</option>
                  <option value="100% Against Dispatch">100% Against Dispatch</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Delivery Incoterms 2020 *
                </label>
                <select
                  value={incoterms}
                  onChange={(e) => setIncoterms(e.target.value)}
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-semibold text-foreground focus:ring-2 focus:ring-primary font-poppins"
                >
                  <option value="DDP">DDP — Delivered Duty Paid (Supplier Risk)</option>
                  <option value="DAP">DAP — Delivered At Place</option>
                  <option value="FOB">FOB — Free On Board</option>
                  <option value="CIF">CIF — Cost, Insurance & Freight</option>
                  <option value="EXW">EXW — Ex Works</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block font-bold text-foreground mb-1.5">
                Special Delivery Instructions & Packing Notes
              </label>
              <textarea
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Enter any mandatory gate pass, dispatch notification, or inspection requirements..."
                className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-medium text-foreground focus:ring-2 focus:ring-primary font-poppins"
              />
            </div>
          </CardContent>
        </Card>

        {/* Action Controls */}
        <div className="flex items-center justify-end gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(ROUTES.PURCHASE_ORDERS.DETAILS(id!))}
            className="font-poppins cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={isSaving}
            className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold font-poppins cursor-pointer px-6 shadow-xs"
          >
            <Save className="h-4 w-4 mr-1.5" /> Save Changes
          </Button>
        </div>
      </form>
    </div>
  );
};
